import { useCallback, useMemo } from "react";

import { imageHost } from "../../config/config";
import { useLanguage } from "../../i18n/useLanguage";
import { VehicleIds } from "../../queries/useGetVehicleMappingByWheelId";
import { VehicleMappingRowItem } from "../../queries/useSearchPartPackagesByVehicle";
import { VehicleManufacturerEntry } from "../../stores/slices/packageEditorSlice";
import { getSubmodelDisplayName } from "../../util/getSubmodelDisplayName";
import { searchPredicate } from "../../util/searchPredicate";
import { createVehicleMappingKey } from "./createVehicleMappingKey";
import { parseVehicleMappingKey } from "./parseVehicleMappingKey";

export const useVehicleMaping = ({
  criteria,
  fallbackManufacturers,
  partId,
  setVehicleMapping,
  vehicleDetails,
  vehicleMapping,
}: {
  criteria: string;
  fallbackManufacturers: VehicleManufacturerEntry[];
  partId: null | string;
  setVehicleMapping(vehicleKeys: string[]): void;
  vehicleDetails: Record<string, VehicleMappingRowItem> | null;
  vehicleMapping: null | string[];
}): VehicleMappingPayload => {
  const [language] = useLanguage();

  const selectedKeys = useMemo(() => {
    const currentKeys = new Set(vehicleMapping || []);
    return currentKeys;
  }, [vehicleMapping?.toString()]);

  const allVehicles: VehicleMappingItem[] = useMemo(() => {
    const vehicleDetailsValues = vehicleDetails
      ? Object.values(vehicleDetails)
      : [];

    const manufacturers = [...vehicleDetailsValues, ...fallbackManufacturers];
    const usedKeys = new Set<string>();

    const vehicleMappingItems: VehicleMappingItem[] = [];

    for (const {
      vehicleManufacturerName,
      vehicleModelId,
      vehicleModelName,
      vehicleSubmodelId,
      vehicleSubmodelImage,
      vehicleSubmodelName,
      vehicleYear,
      vehicleYearId,
    } of manufacturers) {
      const key = createVehicleMappingKey({
        vehicleModelId,
        vehicleSubmodelId,
        vehicleYearId,
      });

      if (usedKeys.has(key)) {
        continue;
      }
      // images.tirelocator.ca/honda/Honda_vehicles/2022_civic_sedan_lx.png
      const vehicleImage =
        vehicleSubmodelImage &&
        `//${imageHost}/${vehicleManufacturerName.toLowerCase()}/${vehicleManufacturerName}_vehicles/${vehicleYear}_${vehicleSubmodelImage}`;

      const vehicleName = [
        vehicleManufacturerName,
        vehicleModelName[language] || vehicleModelName["en"],
      ]
        .filter(Boolean)
        .join(" ");

      const vehicleModel = [
        getSubmodelDisplayName(
          vehicleModelName[language],
          vehicleSubmodelName[language]
        ),
        vehicleYear,
      ]
        .filter(Boolean)
        .join(" ");

      const toggle = (): void => {
        const next = new Set(selectedKeys);
        next.has(key) ? next.delete(key) : next.add(key);
        setVehicleMapping([...next]);
      };

      const mapped: VehicleMappingItem = {
        key,
        selected: selectedKeys.has(key),
        toggle,
        vehicleImage,
        vehicleModel,
        vehicleModelId,
        vehicleName,
        vehicleSubmodelId,
        vehicleYearId,
      };

      vehicleMappingItems.push(mapped);
      usedKeys.add(key);
    }

    return vehicleMappingItems;
  }, [
    partId,
    selectedKeys.size,
    language,
    criteria,
    fallbackManufacturers.length,
    vehicleDetails,
  ]);

  const visibleVehicles = useMemo(
    () =>
      allVehicles.filter(
        ({
          vehicleModel,
          vehicleModelId,
          vehicleName,
          vehicleSubmodelId,
        }): boolean => {
          const result = searchPredicate(
            `${vehicleModel} ${vehicleName} ${vehicleModelId} ${vehicleSubmodelId}`,
            criteria
          );

          return result;
        }
      ),
    [selectedKeys.size, language, allVehicles]
  );

  const selectVisible = useCallback(() => {
    const next = new Set(selectedKeys);
    visibleVehicles.forEach(({ key }) => {
      next.add(key);
    });
    setVehicleMapping([...next]);
  }, [visibleVehicles]);

  const selectNone = useCallback(() => {
    const next = new Set(selectedKeys);
    visibleVehicles.forEach(({ key }) => {
      next.delete(key);
    });
    setVehicleMapping([...next]);
  }, [visibleVehicles]);

  const currentlySelected = visibleVehicles.filter(
    ({ selected }) => selected
  ).length;

  const selectedVehicleIds = [...selectedKeys].map(parseVehicleMappingKey);

  return {
    currentlySelected,
    currentlyShowing: visibleVehicles.length,
    selectNone,
    selectVisible,
    selectedVehicleIds,
    totalAvailable: allVehicles.length,
    totalSelected: selectedKeys.size,
    vehicles: visibleVehicles,
  };
};

export type VehicleMappingPayload = {
  currentlySelected: number;
  currentlyShowing: number;
  selectNone: VoidFunction;
  selectVisible: VoidFunction;
  selectedVehicleIds: VehicleIds[];
  totalAvailable: number;
  totalSelected: number;
  vehicles: VehicleMappingItem[];
};

export type VehicleMappingItem = {
  key: string;
  selected: boolean;
  toggle: VoidFunction;
  vehicleImage?: string;
  vehicleModel: string;
  vehicleModelId: string;
  vehicleName: string;
  vehicleSubmodelId: string;
  vehicleYearId: string;
};

/**
 * @see docs/worklog/TW-1010-edit-package-vehicle-mapping-reset/rfc.md
 * @see docs/worklog/TW-1010-edit-package-vehicle-mapping-reset/tfc.md
 */
